import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../lib/auth';
import type { AuthResponse } from '../lib/auth';
import { API_BASE } from '../lib/apiBase';

const schema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(1, 'Password is required'),
});

type FormData = z.infer<typeof schema>;

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '12px 16px',
  borderRadius: 12,
  border: '1.5px solid #d1d5db',
  fontSize: 16,
  outline: 'none',
  boxSizing: 'border-box',
};

export function LoginPage() {
  const navigate = useNavigate();
  const setAuth = useAuthStore((s) => s.setAuth);

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({ resolver: zodResolver(schema) });

  const onSubmit = async (data: FormData) => {
    try {
      const res = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (!res.ok) {
        const err = await res.json() as { error?: { message: string } };
        setError('root', { message: err.error?.message ?? 'Login failed.' });
        return;
      }

      const body = await res.json() as AuthResponse | { two_factor_required: true };

      if ('two_factor_required' in body) {
        // 2FA-enabled account — hand off credentials to the validation step
        navigate('/app/2fa-verify', {
          state: { email: data.email, password: data.password },
        });
        return;
      }

      setAuth(body.user, body.access_token, body.expires_in);
      navigate('/app/dashboard', { replace: true });
    } catch {
      setError('root', { message: 'Unable to connect. If you are using Brave browser, tap the Brave Shields icon and turn off shields for this site, then try again.' });
    }
  };

  return (
    <div style={{
      minHeight: '100dvh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px 24px',
      background: '#f9fafb',
    }}>
      <div style={{ width: '100%', maxWidth: 360 }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{
            width: 64, height: 64, borderRadius: 16,
            background: '#1D4ED8', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 16px', fontSize: 28,
          }}>
            🏠
          </div>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: '#111827', margin: 0 }}>Welcome back</h1>
          <p style={{ color: '#6b7280', fontSize: 14, marginTop: 4, marginBottom: 0 }}>
            Sign in to your Housewizrd account
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label style={{ display: 'block', fontSize: 14, fontWeight: 500, color: '#374151', marginBottom: 6 }}>
              Email
            </label>
            <input
              {...register('email')}
              type="email"
              autoComplete="email"
              placeholder="you@example.com"
              style={inputStyle}
            />
            {errors.email && (
              <p style={{ color: '#ef4444', fontSize: 12, marginTop: 4 }}>{errors.email.message}</p>
            )}
          </div>

          <div>
            <label style={{ display: 'block', fontSize: 14, fontWeight: 500, color: '#374151', marginBottom: 6 }}>
              Password
            </label>
            <input
              {...register('password')}
              type="password"
              autoComplete="current-password"
              style={inputStyle}
            />
            {errors.password && (
              <p style={{ color: '#ef4444', fontSize: 12, marginTop: 4 }}>{errors.password.message}</p>
            )}
          </div>

          {errors.root && (
            <div style={{
              background: '#fef2f2', border: '1px solid #fecaca',
              borderRadius: 10, padding: '10px 14px',
              color: '#dc2626', fontSize: 14, textAlign: 'center',
            }}>
              {errors.root.message}
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            style={{
              width: '100%', padding: '14px',
              background: isSubmitting ? '#93c5fd' : '#1D4ED8',
              color: '#fff', borderRadius: 12, fontWeight: 600,
              fontSize: 16, border: 'none', cursor: isSubmitting ? 'not-allowed' : 'pointer',
            }}
          >
            {isSubmitting ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        <p style={{ textAlign: 'center', marginTop: 16, fontSize: 14, color: '#6b7280' }}>
          <Link to="/forgot-password" style={{ color: '#1D4ED8', fontWeight: 500, textDecoration: 'none' }}>
            Forgot password?
          </Link>
        </p>

        <p style={{ textAlign: 'center', marginTop: 24, fontSize: 14, color: '#6b7280' }}>
          Don&apos;t have an account?{' '}
          <Link to="/register" style={{ color: '#1D4ED8', fontWeight: 600, textDecoration: 'none' }}>
            Get started free
          </Link>
        </p>

        <ResendVerificationForm />
      </div>
    </div>
  );
}

function ResendVerificationForm() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSending(true);
    setError(null);
    try {
      await fetch(`${API_BASE}/auth/resend-verification-by-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      // Always show the same message — the endpoint never reveals if the email exists
      setDone(true);
    } catch {
      setError('Unable to connect. Check your network and try again.');
    } finally {
      setSending(false);
    }
  }

  if (!open) {
    return (
      <p style={{ textAlign: 'center', marginTop: 16, fontSize: 13, color: '#9ca3af' }}>
        Didn&apos;t receive your verification email?{' '}
        <button
          type="button"
          onClick={() => setOpen(true)}
          style={{
            background: 'none', border: 'none', padding: 0,
            color: '#1D4ED8', fontWeight: 500, fontSize: 13,
            cursor: 'pointer', textDecoration: 'underline',
          }}
        >
          Resend it
        </button>
      </p>
    );
  }

  return (
    <div style={{
      marginTop: 20, padding: '16px', background: '#f9fafb',
      borderRadius: 12, border: '1px solid #e5e7eb',
    }}>
      <p style={{ margin: '0 0 12px', fontWeight: 600, fontSize: 14, color: '#111827' }}>
        Resend verification email
      </p>

      {done ? (
        <p style={{ margin: 0, fontSize: 14, color: '#374151', lineHeight: 1.6 }}>
          If that email is registered we have sent a new verification link.
          Check your inbox and spam folder.
        </p>
      ) : (
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
            required
            style={{
              width: '100%', padding: '10px 14px', borderRadius: 10,
              border: '1.5px solid #d1d5db', fontSize: 14,
              outline: 'none', boxSizing: 'border-box',
            }}
          />
          {error && (
            <p style={{ margin: 0, fontSize: 13, color: '#dc2626' }}>{error}</p>
          )}
          <button
            type="submit"
            disabled={sending}
            style={{
              width: '100%', padding: '10px',
              background: sending ? '#93c5fd' : '#1D4ED8',
              color: '#fff', borderRadius: 10, border: 'none',
              fontSize: 14, fontWeight: 600,
              cursor: sending ? 'not-allowed' : 'pointer',
            }}
          >
            {sending ? 'Sending…' : 'Send verification email'}
          </button>
        </form>
      )}
    </div>
  );
}
